import { envConfig } from "config/envConfig"
import { CallStatus, MessageStatus } from "interface/communications"
import {
	RealtimeTransportEvent,
	useRealtimeTransport,
} from "modules/realtime/useRealtimeTransport"
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from "react"

interface CommsPayload {
	sid?: string
	status?: MessageStatus
	type?: RealtimeTransportEvent
	patientId?: string
	unreadCount?: number
	sender?: string
	callSid?: string
	callStatus?: CallStatus
	timestamp?: string
}

interface TicketPayload {
	ticketId: string
	patientId?: string
}

interface EscalationPayload {
	escalationId: string
	patientId: string
}

interface NotificationPayload {
	unreadCount: number
	timestamp: string
}

type RealtimePayload =
	CommsPayload | TicketPayload | EscalationPayload | NotificationPayload

export interface RealtimeEventPayloadMap extends Record<
	RealtimeTransportEvent,
	RealtimePayload
> {
	message_status_update: CommsPayload
	new_message: CommsPayload
	unread_count_update: CommsPayload
	voice_call_status_update: CommsPayload
	ticket_updated: TicketPayload
	ticket_deleted: TicketPayload
	escalation_updated: EscalationPayload
	notification_unread_count_update: NotificationPayload
}

export type RealtimeEventName = keyof RealtimeEventPayloadMap

export type RealtimeEventHandler<
	E extends RealtimeEventName = RealtimeEventName,
> = (data: RealtimeEventPayloadMap[E], eventName: E) => void | Promise<void>

export interface RealtimeEventBus {
	subscribe: <E extends RealtimeEventName>(
		eventName: E,
		handler: RealtimeEventHandler<E>,
	) => () => void
	publish: <E extends RealtimeEventName>(
		eventName: E,
		data: RealtimeEventPayloadMap[E],
	) => void
}

type RealtimeContextType = RealtimeEventBus

export const RealtimeContext = createContext<RealtimeContextType | null>(null)

export function useRealtimeEventBus() {
	return useContext(RealtimeContext)
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
	const subscribersRef = useRef<
		Map<RealtimeEventName, Set<RealtimeEventHandler<any>>>
	>(new Map())

	const subscribe = useCallback(
		<E extends RealtimeEventName>(
			eventName: E,
			handler: RealtimeEventHandler<E>,
		) => {
			if (!subscribersRef.current.has(eventName)) {
				subscribersRef.current.set(eventName, new Set())
			}
			const set = subscribersRef.current.get(eventName)!
			set.add(handler as RealtimeEventHandler<any>)

			return () => {
				set.delete(handler as RealtimeEventHandler<any>)
				if (set.size === 0) {
					subscribersRef.current.delete(eventName)
				}
			}
		},
		[],
	)

	const publish = useCallback(
		<E extends RealtimeEventName>(
			eventName: E,
			data: RealtimeEventPayloadMap[E],
		) => {
			const listeners = subscribersRef.current.get(eventName)
			if (listeners) {
				Array.from(listeners).forEach((handler) => {
					try {
						const result = handler(data, eventName)
						if (result instanceof Promise) {
							result.catch((err) => {
								console.error(
									"[realtime bus] Async error in handler for event:",
									eventName,
									err,
								)
							})
						}
					} catch (err) {
						console.error(
							"[realtime bus] Error in handler for event:",
							eventName,
							err,
						)
					}
				})
			}
		},
		[],
	)

	const contextValue = useMemo<RealtimeContextType>(
		() => ({
			subscribe,
			publish,
		}),
		[subscribe, publish],
	)

	useRealtimeTransport<RealtimePayload>({
		serverUrl: envConfig.baseApiUrl(),
		manual: false,
		onMessage: async (eventName, data) => {
			console.debug(
				"[realtime] Received event from transport:",
				eventName,
				data,
			)
			publish(eventName as RealtimeEventName, data as any)
		},
	})

	return <RealtimeContext value={contextValue}>{children}</RealtimeContext>
}
